import { TextLink } from '@rsa-digital/evo-shared-components/commonTypes/links';
import { CardColumnSpanCalculator } from '@rsa-digital/evo-shared-components/components/CardGrid';
import { InfoCard } from '@rsa-digital/evo-shared-components/components/InfoCard';
import { columnSpanCalculator } from '@rsa-digital/evo-shared-components/components/InfoCard/InfoCardGrid';
import { graphql } from 'gatsby';
import React from 'react';
import {
  processImageAsset,
  processOptionalCta,
  unwrapSingleton,
} from 'helpers/csTypeProcessors';
import useOptimizelyVariation from 'helpers/useOptimizelyVariation';
import { CsAsset, CsCta, CsIcon } from 'types/contentStack';
import { StyledInfoCardGrid } from './styles';

type CsBreakpoint = 'desktop' | 'tablet_landscape' | 'tablet_portrait' | 'mobile';

type CsColumnsByBreakpoint = { [K in CsBreakpoint]: number };

type CardsSettings = {
  align_center: boolean;
  align_cards_left: boolean;
  column_span: CsColumnsByBreakpoint;
};

type Link = {
  url: string | null;
  text: string | null;
  open_in_new_tab: boolean;
};

type InfoCardsBlockProps = {
  info_cards: {
    html_id?: string;
    heading?: string;
    intro?: string;
    display_background: boolean;
    cards_settings: CardsSettings | null;
    info_cards: {
      header_icon: [CsIcon] | [];
      header_text: string;
      card_image: CsAsset | null;
      image: CsAsset | null;
      body_text: string;
      cta: [CsCta] | null;
      secondary_cta: [CsCta] | null;
      link: Link | null;
    }[];
  };
};

const processInfoCardsColumnSpan = (
  cardsSettings: CardsSettings | null,
  defaultColumnSpanCalculator: CardColumnSpanCalculator
): CardColumnSpanCalculator => {
  if (cardsSettings === null || cardsSettings.column_span === null) {
    return defaultColumnSpanCalculator;
  }
  return (index, totalCards) => {
    const { desktop, tabletLandscape, tabletPortrait, mobile } =
      defaultColumnSpanCalculator(index, totalCards);
    return {
      desktop: cardsSettings.column_span.desktop ?? desktop,
      tabletLandscape: cardsSettings.column_span.tablet_landscape ?? tabletLandscape,
      tabletPortrait: cardsSettings.column_span.tablet_portrait ?? tabletPortrait,
      mobile: cardsSettings.column_span.mobile ?? mobile,
    };
  };
};

const processInfoCardsLink = (
  link: Link | null
): (TextLink & { icon?: string }) | undefined => {
  if (link === null || !link?.text || !link?.url) {
    return undefined;
  }
  return {
    url: link.url,
    text: link.text,
    icon: link.open_in_new_tab ? 'new-tab' : 'chevron',
  };
};

const InfoCardsBlock: React.FC<InfoCardsBlockProps> = ({ info_cards }) => {
  const { cards_settings } = info_cards;
  const applyInfoCardChanges = useOptimizelyVariation(
    process.env.GATSBY_OPTIMIZELY_RIBBON_BANNER_EXPERIMENT
  );

  const alignInfoCardsContentLeft =
    applyInfoCardChanges && cards_settings !== null ? !cards_settings.align_center : true;

  const infoCardsColumnSpan = processInfoCardsColumnSpan(
    cards_settings,
    columnSpanCalculator
  );

  // Inlcusion of the column span calculator was initially part of an experiment support and will now be used on the category pages.
  // Since the experiment is still ongoing on the homepage, we first check if info cards content is set to be aligned to the center
  // so we can be able to use the column span calculator outside the experiment.
  const infoCardsColumnSpanOnVariantOnly = applyInfoCardChanges
    ? infoCardsColumnSpan
    : undefined;

  const infoCardsColumnSpanCalculator = !cards_settings?.align_center
    ? infoCardsColumnSpan
    : infoCardsColumnSpanOnVariantOnly;

  return (
    <StyledInfoCardGrid
      id={info_cards.html_id}
      data-cy="InfoCards"
      displayBackground={info_cards.display_background}
      heading={info_cards.heading}
      intro={info_cards.intro}
      columnSpanCalculator={infoCardsColumnSpanCalculator}
      alignLeft={cards_settings?.align_cards_left}>
      {info_cards.info_cards.map(
        (
          {
            header_text,
            header_icon,
            body_text,
            cta,
            secondary_cta,
            link,
            image,
            card_image,
          },
          index
        ) => (
          <InfoCard
            data-cy={`InfoCard${index}`}
            key={`${header_text}-${index}`}
            headerIcon={unwrapSingleton(header_icon)?.icon_code}
            heading={header_text}
            body={body_text}
            primaryCta={processOptionalCta(cta, 'infoCardCta')}
            secondaryCta={processOptionalCta(secondary_cta, 'infoCardCta')}
            image={processImageAsset(card_image)}
            logo={processImageAsset(image)}
            displayBackground={info_cards.display_background}
            alignLeft={alignInfoCardsContentLeft}
            link={processInfoCardsLink(link)}
          />
        )
      )}
    </StyledInfoCardGrid>
  );
};

export default React.memo(InfoCardsBlock);

export const query = graphql`
  fragment InfoCardsBlock on cs__info_cards {
    html_id
    heading
    intro
    display_background
    cards_settings {
      align_center
      align_cards_left
      column_span {
        desktop
        tablet_portrait
        tablet_landscape
        mobile
      }
    }
    info_cards {
      header_icon {
        ...Icon
      }
      header_text
      card_image {
        ...Asset
      }
      image {
        ...Asset
      }
      body_text
      cta {
        ...Cta
      }
      secondary_cta {
        ...Cta
      }
      link {
        url
        text
        open_in_new_tab
      }
    }
  }
`;
